import * as React from "react";
import { Button, Form, Grid } from "semantic-ui-react";
import { Input } from "@neworbit/simpleui-input";

import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";
import { IsLoadingButton } from "@teamprevent/common/IsLoadingButton";

import { useDispatch, useSelector } from "@teamprevent/customer-redux";

import { FileUpload } from "@teamprevent/common/fileUploads";

import { acceptableExtensions } from "@teamprevent/common/acceptableExtensions";

import { validators } from "not-valid";

import { ValidationResultType } from "not-valid/bin/results";

import { toast } from "@teamprevent/common/toasts";

import { actionInsufficientInformationTask } from "../actions";
import { ApplicationState } from "../../applicationState";
import { ohCaseSelector } from "../../crud/ohCase/selectors";
import { OhCaseAuthorizer } from "../../crud/ohCase/OhCaseAuthorizer";
import { ACTION_INSUFFICIENT_INFORMATION_TASK } from "../actionTypes";

import { InsufficientTaskDocumentModel } from "../models";

import { OldInsufficientInformationActionForm } from "./OldInsufficientInformationActionForm";

const notesLengthValidator = validators.validLength({ max: 250, message: "Max 250 characters" });

export const NewInsufficientInformationActionForm: React.FunctionComponent= () => {
    const dispatch = useDispatch();

    const currentUser = useSelector((state: ApplicationState) => state.currentUser);
    const insufficientInformationTaskId = useSelector((state: ApplicationState) => state.actionableTask && state.actionableTask.taskId);
    const ohCase = useSelector((state: ApplicationState) => ohCaseSelector(state));
    const [documents, setDocuments] = React.useState<InsufficientTaskDocumentModel[]>([]);
    const [notes, setNotes] = React.useState<string>("");

    const onSubmit = React.useCallback(() => {
        if (!notes) {
            toast.warning("Please enter comments");
            return;
        }

        const notesLengthValidationResult = notesLengthValidator(notes);
        if (notesLengthValidationResult.type !== ValidationResultType.Pass) {
            return;
        }

        const model = {
            insufficientInformationTaskId,
            ohCaseId: ohCase.id,
            notes,
            documents
        };
        dispatch(actionInsufficientInformationTask(model));
    }, [dispatch, documents, insufficientInformationTaskId, notes, ohCase]);

    const documentUploaded = React.useCallback((ohCaseDocumentId: string, fileName: string) => {
        setDocuments((prevDocuments) => [...prevDocuments, { blobId: ohCaseDocumentId, fileName }]);
    }, []);

    const removeEvidence = (docId: string): void => {
        setDocuments((prevDocuments) => [...prevDocuments.filter((doc) => doc.blobId !== docId)]);
    };

    if (!OhCaseAuthorizer.isAuthorizedToUpdate(ohCase, currentUser)) {
        return null;
    }

    return (
        <Form className="tasks-actions" as={Grid}>
            <Grid.Row>
                <Grid.Column width={16}>
                    <Input.Textarea
                        value={notes}
                        label="Comments"
                        onChange={setNotes}
                        validation={[notesLengthValidator]}
                        required
                    />
                </Grid.Column>
            </Grid.Row>
            <Grid.Row textAlign="right">
                <Grid.Column width={16}>
                    <FileUpload
                        apiUrl={`/api/actionabletask/case/${ohCase.id}/insufficientTaskDocument`}
                        onFileChange={documentUploaded}
                        acceptExtensions={acceptableExtensions}
                        showFileName={false}
                        label="Add Document"
                    />
                </Grid.Column>
            </Grid.Row>
            <Grid.Row>
                <Grid.Column width={16}>
                    {documents.map((doc: InsufficientTaskDocumentModel, index) => (
                        <Grid key={index}>
                            <Grid.Row columns={2}>
                                <Grid.Column>
                                    <p key={index}>{doc.fileName}</p>
                                </Grid.Column>
                                <Grid.Column>
                                    <Button
                                        className="remove-evidence-button"
                                        content="Remove"
                                        type="button"
                                        // eslint-disable-next-line react/jsx-no-bind
                                        onClick={() => removeEvidence(doc.blobId)}
                                        floated="right"
                                        color="red"
                                    />
                                </Grid.Column>
                            </Grid.Row>
                        </Grid>
                    ))}
                </Grid.Column>
            </Grid.Row>
            <Grid.Row textAlign="center">
                <Grid.Column width={6} stretched>
                    <IsLoadingButton
                        content="Submit"
                        onClick={onSubmit}
                        className="button"
                        color="green"
                        requestNames={ACTION_INSUFFICIENT_INFORMATION_TASK}
                    />
                </Grid.Column>
            </Grid.Row>
        </Form>
    );
};

export const InsufficientInformationActionForm = () =>
    getFeatureToggles()[FeatureToggleNames.EnableEnhancedInsufficientInformationFunctionality]
        ? <NewInsufficientInformationActionForm />
        : <OldInsufficientInformationActionForm />; // eslint-disable-line deprecation/deprecation
